import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { PinnedOffersConfig, listPinnedOffersConfigs } from '~/services/PublicOfferFeedService';

import PinnedOffersConfigTab from './PinnedOffersConfigTab';

type DistanceLookup = '10km' | '100km' | '1000km' | 'country_radius' | undefined;

function PinnedOffersConfigContainer() {
  const tenant = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<Array<PinnedOffersConfig>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const [selectedCountry, setSelectedCountry] = useState<{ code: string; label: string } | null>(null);
  const [distanceLookup, setDistanceLookup] = useState<DistanceLookup>(undefined);

  const [totalCount, setTotalCount] = useState(0);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      region: selectedCountry?.code || undefined,
      distance_lookup: distanceLookup || undefined,
    });
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
  }, [pageNum, pageSize, selectedCountry, distanceLookup]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listPinnedOffersConfigs({
        page: pageNum,
        limit: pageSize,
        offer_country: selectedCountry?.code || undefined,
        distance_lookup: distanceLookup || undefined,
        brand: tenant.tenant.brand,
      });
      const result = response?.result;
      setData(result?.configs ?? []);
      setTotalCount(Number(response?.count ?? 0));
      setIsLoading(false);
      setQueryParams();
    } catch (error) {
      enqueueSnackbar('Failed to fetch Pinned Offers Config', { variant: 'error' });
      setIsLoading(false);
    }
  }, [pageNum, pageSize, selectedCountry?.code, distanceLookup, tenant.tenant.brand, setQueryParams, enqueueSnackbar]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(timer);
  }, [pageNum, pageSize, selectedCountry, distanceLookup, fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      fetchData();
    },
    [fetchData],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Pinned Offers Config</title>
      </Helmet>
      <PageSubheader title={`Pinned Offers Config [${data?.length} of ${totalCount}]`} />

      <PinnedOffersConfigTab
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        distanceLookup={distanceLookup}
        setDistanceLookup={setDistanceLookup}
        data={data}
        isLoading={isLoading}
        totalCount={totalCount}
        pageNum={pageNum}
        pageSize={pageSize}
        onPageChange={onPageChange}
        fetchData={fetchData}
      />
    </Container>
  );
}

export default PinnedOffersConfigContainer;
